import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import useBlockStyles from '../common/useBlockStyles';
import { resolveLink, isInternalLink } from '../../../../utils/linkResolver';

const STYLE_OPTIONS = {
  defaultBackground: 'card',
  defaultHeadingSize: 'medium',
  defaultAlignment: 'left',
  defaultButtonStyle: 'primary',
  defaultPadding: 'normal',
};

const TextImageRenderer = ({ block, theme = {}, username }) => {
  const { content } = block;
  const {
    heading,
    body,
    image,
    imageAlt = '',
    layout = 'imageRight',
    buttonText = '',
    buttonLink = '',
    imageSize = 'medium',
  } = content;

  const styles = useBlockStyles(content, theme, STYLE_OPTIONS);
  const isOwn = !window.location.pathname.startsWith('/me/');
  const resolvedHref = resolveLink(buttonLink, username, isOwn);
  const useRouterLink = isInternalLink(resolvedHref);

  const isImageTop = layout === 'imageTop';
  const isImageLeft = layout === 'imageLeft';

  // Image size classes - min/max heights that preserve aspect ratio
  const imageSizeClasses = {
    small: 'min-h-32 min-w-32 max-h-48 sm:max-h-56 md:max-h-64',
    medium: 'min-h-40 min-w-40 max-h-64 sm:max-h-80 md:max-h-96',
    large: 'min-h-48 min-w-48 max-h-80 sm:max-h-96 md:max-h-[28rem]',
    auto: 'min-h-32 min-w-32',
  };

  const imageClass = imageSizeClasses[imageSize] || imageSizeClasses.medium;

  // For imageTop layout, stack vertically
  if (isImageTop) {
    return (
      <div
        className={`w-full ${styles.sectionPaddingClass}`}
        style={{ backgroundColor: styles.sectionBackground }}
      >
        <div className="max-w-4xl mx-auto">
          {image && (
            <div className="mb-6 sm:mb-8 rounded-lg overflow-hidden flex justify-center">
              <img
                src={image}
                alt={imageAlt || heading || 'Section image'}
                className={`max-w-full h-auto ${imageClass} object-contain`}
              />
            </div>
          )}

          <div className={`${styles.headingAlignmentClass}`}>
            {heading && (
              <h3
                className={`${styles.headingSizeClass} font-bold mb-3 sm:mb-4`}
                style={{ color: styles.headingColor, fontFamily: styles.headingFontStyle }}
              >
                {heading}
              </h3>
            )}
            {body && (
              <p
                className="text-base sm:text-lg leading-relaxed whitespace-pre-wrap opacity-90"
                style={{ color: styles.bodyColor, fontFamily: styles.bodyFontStyle }}
              >
                {body}
              </p>
            )}
            {buttonText && (
              useRouterLink ? (
                <Link
                  to={resolvedHref}
                  className="inline-flex items-center justify-center gap-2 mt-6 px-5 sm:px-6 py-3 text-white font-semibold rounded-lg transition-all hover:opacity-90 text-sm sm:text-base"
                  style={styles.getButtonStyles()}
                >
                  {buttonText}
                  <ArrowRight size={18} />
                </Link>
              ) : (
                <a
                  href={resolvedHref}
                  className="inline-flex items-center justify-center gap-2 mt-6 px-5 sm:px-6 py-3 text-white font-semibold rounded-lg transition-all hover:opacity-90 text-sm sm:text-base"
                  style={styles.getButtonStyles()}
                >
                  {buttonText}
                  <ArrowRight size={18} />
                </a>
              )
            )}
          </div>
        </div>
      </div>
    );
  }

  // For side-by-side layouts
  return (
    <div
      className={`w-full ${styles.sectionPaddingClass}`}
      style={{ backgroundColor: styles.sectionBackground }}
    >
      <div className="max-w-6xl mx-auto">
        <div className={`grid grid-cols-1 ${image ? 'md:grid-cols-2' : ''} gap-6 sm:gap-8 md:gap-12 items-center`}>
          {image && (
            <div className={`${isImageLeft ? 'md:order-1' : 'md:order-2'} flex justify-center`}>
              <div className="rounded-lg overflow-hidden">
                <img
                  src={image}
                  alt={imageAlt || heading || 'Section image'}
                  className={`max-w-full h-auto ${imageClass} object-contain`}
                />
              </div>
            </div>
          )}

          <div className={`${isImageLeft ? 'md:order-2' : 'md:order-1'}`}>
            {heading && (
              <h3
                className={`${styles.headingSizeClass} font-bold mb-3 sm:mb-4`}
                style={{ color: styles.headingColor, fontFamily: styles.headingFontStyle }}
              >
                {heading}
              </h3>
            )}
            {body && (
              <p
                className="text-base sm:text-lg leading-relaxed whitespace-pre-wrap opacity-90"
                style={{ color: styles.bodyColor, fontFamily: styles.bodyFontStyle }}
              >
                {body}
              </p>
            )}
            {buttonText && (
              useRouterLink ? (
                <Link
                  to={resolvedHref}
                  className="inline-flex items-center justify-center gap-2 mt-6 px-5 sm:px-6 py-3 text-white font-semibold rounded-lg transition-all hover:opacity-90 text-sm sm:text-base"
                  style={styles.getButtonStyles()}
                >
                  {buttonText}
                  <ArrowRight size={18} />
                </Link>
              ) : (
                <a
                  href={resolvedHref}
                  className="inline-flex items-center justify-center gap-2 mt-6 px-5 sm:px-6 py-3 text-white font-semibold rounded-lg transition-all hover:opacity-90 text-sm sm:text-base"
                  style={styles.getButtonStyles()}
                >
                  {buttonText}
                  <ArrowRight size={18} />
                </a>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TextImageRenderer;
